/* eslint-disable no-console */

import { register } from 'register-service-worker'

// Enhanced PWA update detection for existing installations
let isFirstLoad = true;
let updateCheckInterval = null;

// Global flag to prevent multiple update prompts
let updatePromptShowing = false;

if (process.env.NODE_ENV === 'production') {
  // Clean up any stuck update states on app start
  cleanupUpdateStates();
  
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready () {
      console.log('🎯 App is being served from cache by a service worker.')
      
      // Start periodic update checks for existing PWAs
      startPeriodicUpdateCheck();
    },
    registered (registration) {
      console.log('📝 Service worker has been registered.')
      
      // Store registration for manual updates
      window.swRegistration = registration;
      
      // Disable automatic update checking to prevent loops
      // TODO: Re-enable with proper user consent after testing
      // setInterval(() => {
      //   console.log('🔍 Checking for SW updates...');
      //   registration.update();
      // }, 1000 * 60 * 60) // Check every hour (when re-enabled)
    },
    cached () {
      console.log('💾 Content has been cached for offline use.')
    },
    updatefound (registration) {
      console.log('🔄 New content is downloading...')
      
      const currentVersion = process.env.VUE_APP_VERSION || 'New Version';
      const lastVersion = localStorage.getItem('last_app_version');
      
      // Only show downloading notification if it's actually a new version
      if (lastVersion && lastVersion === currentVersion) {
        console.log('🔄 Same version downloading, skipping notification');
        return;
      }
      
      // Trigger the update notification system
      const updateNotificationService = require('@/services/updateNotificationService').default;
      updateNotificationService.addUpdateNotification({
        version: currentVersion,
        type: 'available',
        message: 'A new version is downloading. You will be notified when ready to install.',
        timestamp: new Date().toISOString()
      });
      
      // Dispatch custom event for components
      window.dispatchEvent(new CustomEvent('app-update-available', {
        detail: { version: currentVersion }
      }));
      
      // Show subtle update notification
      if (window.Vue && window.Vue.prototype.$swal) {
        window.Vue.prototype.$swal({
          title: 'Update Downloading',
          text: 'A new version is being prepared...',
          icon: 'info',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        })
      }
    },
    updated (registration) {
      console.log('✨ New content is available!')
      
      // Prevent multiple prompts
      if (updatePromptShowing) {
        console.log('🔄 Update prompt already showing, skipping');
        return;
      }
      
      // Show update prompt with current version info
      const currentVersion = process.env.VUE_APP_VERSION || 'Unknown';
      const lastVersion = localStorage.getItem('last_app_version');
      
      // Prevent update loop - only show if version actually changed
      if (lastVersion && lastVersion === currentVersion) {
        console.log('🔄 Same version detected, skipping update prompt to prevent loop');
        return;
      }
      
      // Check if user already dismissed this version
      if (localStorage.getItem('update_dismissed_' + currentVersion)) {
        console.log('🔄 User already dismissed this version, skipping');
        return;
      }
      
      // Store current version to prevent loops
      localStorage.setItem('last_app_version', currentVersion);
      
      // Set flag to prevent multiple prompts
      updatePromptShowing = true;
      
      // Trigger the update notification system
      const updateNotificationService = require('@/services/updateNotificationService').default;
      updateNotificationService.addUpdateNotification({
        version: currentVersion,
        type: 'ready',
        message: `Version ${currentVersion} is ready to install. Refresh to update.`,
        timestamp: new Date().toISOString()
      });
      
      // Dispatch custom event for components
      window.dispatchEvent(new CustomEvent('app-update-downloaded', {
        detail: { version: currentVersion }
      }));
      
      if (window.Vue && window.Vue.prototype.$swal) {
        window.Vue.prototype.$swal({
          title: 'Update Ready! 🚀',
          html: `
            <p>A new version is ready to install.</p>
            <small style="color: #666;">New: v${currentVersion}</small>
            ${lastVersion ? `<br><small style="color: #888;">Previous: v${lastVersion}</small>` : ''}
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Update Now',
          cancelButtonText: 'Later',
          confirmButtonColor: '#4CAF50',
          cancelButtonColor: '#6c757d'
        }).then((result) => {
          // Clear the flag when dialog closes
          updatePromptShowing = false;
          
          if (result.isConfirmed) {
            // Mark as updating to prevent multiple prompts
            localStorage.setItem('app_updating', 'true');
            
            // Force update
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' })
            }
            
            // Show loading and reload
            window.Vue.prototype.$swal({
              title: 'Updating...',
              text: 'Please wait while we update the app.',
              allowOutsideClick: false,
              showConfirmButton: false,
              willOpen: () => {
                window.Vue.prototype.$swal.showLoading()
              }
            })
            
            setTimeout(() => {
              window.location.reload()
            }, 1500)
          } else {
            // User cancelled, don't show again for this version
            localStorage.setItem('update_dismissed_' + currentVersion, 'true');
            localStorage.setItem('update_dismissed_' + currentVersion + '_timestamp', Date.now().toString());
          }
        }).catch(() => {
          // Clear flag if dialog fails
          updatePromptShowing = false;
        });
      } else {
        // Clear flag if no SweetAlert
        updatePromptShowing = false;
      }
    },
    offline () {
      console.log('📱 No internet connection. Running in offline mode.')
      showOfflineNotification();
    },
    error (error) {
      console.error('❌ Error during service worker registration:', error)
      
      // Try to recover from SW errors
      handleServiceWorkerError(error);
    }
  })
}

// Enhanced update notification system
function showUpdateNotification(type) {
  const currentVersion = process.env.VUE_APP_VERSION || 'Unknown';
  
  if (type === 'downloading') {
    if (window.Vue && window.Vue.prototype.$swal) {
      window.Vue.prototype.$swal({
        title: 'Update Downloading',
        text: 'A new version is being prepared...',
        icon: 'info',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    } else {
      // Fallback for when Vue/SweetAlert isn't ready
      console.log('📱 PWA Update: New version downloading...');
      showNativeNotification('📱 App Update', 'New version downloading...');
    }
  }
}

function showUpdatePrompt(registration) {
  const currentVersion = process.env.VUE_APP_VERSION || 'Unknown';
  
  if (window.Vue && window.Vue.prototype.$swal) {
    window.Vue.prototype.$swal({
      title: 'Update Ready! 🚀',
      html: `
        <div style="text-align: center;">
          <p><strong>A new version is ready to install.</strong></p>
          <p style="color: #666; font-size: 14px;">Current: v${currentVersion}</p>
          <p style="color: #28a745; font-size: 12px;">✨ Latest features and improvements available</p>
        </div>
      `,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Update Now',
      cancelButtonText: 'Later',
      confirmButtonColor: '#4CAF50',
      cancelButtonColor: '#6c757d',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        applyUpdate(registration);
      } else {
        // Remind user later
        setTimeout(() => {
          showUpdateReminder(registration);
        }, 1000 * 60 * 10); // Remind in 10 minutes
      }
    });
  } else {
    // Fallback: Use native browser notification
    console.log('🚀 PWA Update: New version ready!');
    const shouldUpdate = confirm(`🚀 New app version available!\nCurrent: v${currentVersion}\n\nUpdate now?`);
    if (shouldUpdate) {
      applyUpdate(registration);
    }
  }
}

function showUpdateReminder(registration) {
  if (window.Vue && window.Vue.prototype.$swal) {
    window.Vue.prototype.$swal({
      title: 'Update Reminder',
      text: 'Don\'t forget to update to the latest version!',
      icon: 'info',
      toast: true,
      position: 'top-end',
      showConfirmButton: true,
      confirmButtonText: 'Update',
      timer: 8000,
      timerProgressBar: true
    }).then((result) => {
      if (result.isConfirmed) {
        applyUpdate(registration);
      }
    });
  }
}

function applyUpdate(registration) {
  if (window.Vue && window.Vue.prototype.$swal) {
    window.Vue.prototype.$swal({
      title: 'Updating...',
      text: 'Please wait while we update the app.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => {
        window.Vue.prototype.$swal.showLoading();
      }
    });
  }
  
  // Force update
  if (registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
  
  // Reload after a short delay
  setTimeout(() => {
    window.location.reload();
  }, 1500);
}

function showOfflineNotification() {
  if (window.Vue && window.Vue.prototype.$swal) {
    window.Vue.prototype.$swal({
      title: 'Offline Mode',
      text: 'You\'re currently offline. Some features may be limited.',
      icon: 'warning',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true
    });
  }
}

function handleServiceWorkerError(error) {
  console.error('Service Worker Error Details:', error);
  
  // Try to unregister and re-register SW
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (let registration of registrations) {
        registration.unregister().then(() => {
          console.log('🔄 Unregistered faulty service worker, reloading...');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        });
      }
    });
  }
}

function showNativeNotification(title, body) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/img/icons/android-chrome-192x192.png' });
  }
}

function startPeriodicUpdateCheck() {
  // Check for updates every 30 minutes
  setInterval(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CHECK_FOR_UPDATES' });
    }
  }, 1000 * 60 * 30);
}

// Clean up stuck update states
function cleanupUpdateStates() {
  try {
    // Clear updating flag if it exists
    const isUpdating = localStorage.getItem('app_updating');
    if (isUpdating) {
      localStorage.removeItem('app_updating');
      console.log('🧹 Cleared stuck updating state');
    }
    
    // Clean up old dismissed notifications (older than 7 days)
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith('update_dismissed_')) {
        const timestamp = localStorage.getItem(key + '_timestamp');
        if (!timestamp || parseInt(timestamp) < oneWeekAgo) {
          localStorage.removeItem(key);
          if (timestamp) localStorage.removeItem(key + '_timestamp');
        }
      }
    });
    
    console.log('🧹 Update state cleanup completed');
  } catch (error) {
    console.error('Error cleaning up update states:', error);
  }
}

// Enhanced version checking
function isNewVersionAvailable(newVersion) {
  const currentVersion = localStorage.getItem('last_app_version');
  if (!currentVersion) return true;
  
  // Simple version comparison - you can make this more sophisticated
  return newVersion !== currentVersion;
}

// Listen for service worker messages with enhanced handling
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('📨 SW Message received:', event.data);
    
    if (event.data && event.data.type === 'SW_UPDATED') {
      console.log(`🎉 Service Worker updated to version: ${event.data.version}`);
      
      // Store version info for footer display
      localStorage.setItem('app_version', event.data.version);
      localStorage.setItem('app_build_time', event.data.buildTime);
      
      // Trigger custom event for components
      window.dispatchEvent(new CustomEvent('sw-updated', {
        detail: { 
          version: event.data.version,
          buildTime: event.data.buildTime,
          isUpdate: event.data.isUpdate || false
        }
      }));
      
      // Show success notification for updates
      if (event.data.isUpdate && window.Vue && window.Vue.prototype.$swal) {
        setTimeout(() => {
          window.Vue.prototype.$swal({
            title: 'Update Complete! ✅',
            text: `App updated to version ${event.data.version}`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true
          });
        }, 2000);
      }
    }
    
    // Handle force update messages from Firebase push notifications
    if (event.data && event.data.type === 'FORCE_UPDATE') {
      console.log('🚀 Force update requested from push notification:', event.data.version);
      
      // Show immediate update prompt
      if (window.Vue && window.Vue.prototype.$swal) {
        window.Vue.prototype.$swal({
          title: '🚀 App Update Ready!',
          html: `
            <p>Version ${event.data.version} is ready to install.</p>
            <p><strong>This update will be applied immediately.</strong></p>
            <small>Triggered by push notification</small>
          `,
          icon: 'info',
          showCancelButton: false,
          confirmButtonText: 'Update Now',
          confirmButtonColor: '#4CAF50',
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(() => {
          // Force reload to apply update
          window.location.reload();
        });
      } else {
        // Fallback for when SweetAlert is not available
        if (confirm(`App update ${event.data.version} is ready. Update now?`)) {
          window.location.reload();
        }
      }
    }
    
    // Handle update available messages from Firebase push notifications
    if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
      console.log('📱 Update available notification from push:', event.data.version);
      
      // Dispatch event for app update notifications component
      window.dispatchEvent(new CustomEvent('app-update-available', {
        detail: { 
          version: event.data.version,
          fromNotification: true,
          status: 'downloading'
        }
      }));
    }
  });
  
  // Request version info on load
  navigator.serviceWorker.ready.then(registration => {
    if (registration.active) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        if (event.data.version) {
          localStorage.setItem('app_version', event.data.version);
          localStorage.setItem('app_build_time', event.data.buildTime);
          
          window.dispatchEvent(new CustomEvent('sw-version-ready', {
            detail: event.data
          }));
        }
      };
      
      registration.active.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    }
  });
}

// Handle page visibility changes for update checks
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && window.swRegistration) {
    console.log('🔍 Page visible again, checking for updates...');
    window.swRegistration.update();
  }
});

// Request notification permission for better UX
if ('Notification' in window && Notification.permission === 'default') {
  Notification.requestPermission().then(permission => {
    console.log('📱 Notification permission:', permission);
  });
}
